import React, { useMemo } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft } from 'react-feather';

/**
 * Breadcrumbs Component
 *
 * Props:
 * - current: Optional label for the current breadcrumb segment.
 * - baseQueryParamKey: The query parameter key that holds the base value for the deep link (default: "previous_page").
 * - allowedQueryParams: Optional array of query parameter keys to include in the deep link.
 *   If not provided, all query parameters except the baseQueryParamKey will be included.
 * - fallbackPath: Optional fallback path if the deep link cannot be computed.
 */
export const Breadcrumbs = ({
  current,
  baseQueryParamKey = 'previous_page',
  allowedQueryParams = null,
  fallbackPath = null,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Split the pathname into segments
  const pathnames = location.pathname.split('/').filter(Boolean);

  // Compute the deep link (memoized)
  const deepLink = useMemo(() => {
    const baseValue = searchParams.get(baseQueryParamKey);
    if (!baseValue) return null;

    const queryParams = new URLSearchParams();

    if (allowedQueryParams && Array.isArray(allowedQueryParams)) {
      // Only include allowed keys if provided
      allowedQueryParams.forEach((key) => {
        const value = searchParams.get(key);
        if (value) queryParams.append(key, value);
      });
    } else {
      // Otherwise include all query params except the base key
      for (const [key, value] of searchParams.entries()) {
        if (key !== baseQueryParamKey) {
          queryParams.append(key, value);
        }
      }
    }
    const queryString = queryParams.toString();
    return `/app/${baseValue}${queryString ? '?' + queryString : ''}`;
  }, [searchParams, baseQueryParamKey, allowedQueryParams]);

  // Fallback autoPath: use parent path from current URL if no deepLink or fallbackPath provided
  const autoPath = pathnames.length > 1 ? `/${pathnames.slice(0, -1).join('/')}` : '/';
  const prevPath = deepLink || fallbackPath || autoPath;

  return (
    <nav className="flex justify-between items-center rounded-lg" aria-label="Breadcrumbs">
      {/* Breadcrumb Links */}
      <ol className="flex space-x-2 text-sm text-gray-600 dark:text-gray-400">
        {pathnames.map((segment, index) => {
          const to = `/${pathnames.slice(0, index + 1).join('/')}`;
          const isLast = index === pathnames.length - 1;
          return (
            <li key={to} className="flex items-center">
              {index > 0 && <span className="mx-2 text-gray-500">/</span>}
              {isLast ? (
                <span className="text-gray-500 dark:text-gray-300 font-medium">
                  {current || segment}
                </span>
              ) : (
                <Link to={to} className="text-gray-500 hover:text-blue-600 dark:hover:text-blue-400">
                  {segment === 'app' ? 'Overview' : segment}
                </Link>
              )}
            </li>
          );
        })}
      </ol>

      {pathnames.length > 0 && (
        <button
          onClick={() => navigate(prevPath)}
          className="flex items-center space-x-2 py-1 text-sm transition"
        >
          <motion.div
            animate={{ x: [0, 3, -3, 0] }}
            transition={{
              duration: 0.6,
              repeat: Infinity,
              repeatType: 'loop',
              ease: 'easeInOut',
            }}
          >
            <ChevronLeft size={12} />
          </motion.div>
          <span>Back</span>
        </button>
      )}
    </nav>
  );
};
